import { type ComponentPropsWithRef, forwardRef, type ReactNode } from "react";
import { Label } from "@/core/shared/v2/ui/label";
import { Select } from "@/core/shared/v2/ui/select";

export interface SelectFieldProps extends Omit<ComponentPropsWithRef<"select">, "required"> {
  label?: ReactNode;
  error?: string;
  /** Renders the `*` asterisk in the label only — see TextField note on yup-only validation. */
  required?: boolean;
}

/** Label + v2 Select + error text. `children` are `<Select.Option>` elements. */
export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  ({ label, error, required, name, id, children, ...props }, ref) => {
    const fieldId = id ?? name;
    const errorId = error && fieldId ? `${fieldId}-error` : undefined;

    return (
      <div className="flex flex-col gap-2">
        {label && (
          <Label htmlFor={fieldId}>
            {label}
            {required ? " *" : ""}
          </Label>
        )}
        <Select ref={ref} id={fieldId} name={name} aria-invalid={!!error} aria-describedby={errorId} {...props}>
          {children}
        </Select>
        {error && (
          <p id={errorId} role="alert" className="font-medium text-destructive text-sm">
            {error}
          </p>
        )}
      </div>
    );
  },
);

SelectField.displayName = "SelectField";
